import { forwardRef, useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';

import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerProps,
  Typography,
  Stack,
  tokens,
  colorOrToken,
  Alert,
} from '@cast/design-system';
import { useRegionsQuery } from '@cast/shared-inventory-data-access';
import { Cloud } from '@cast/shared-types';

import { PreviewJson } from 'components/code';
import { Detail } from 'components/drawer';
import { ExpandablePanel } from 'components/expandable-panel';

import { EdgeLocationConfigurationsSection } from './EdgeLocationConfigurationsSection';
import { EdgeLocationNodesSection } from './EdgeLocationNodesSection';
import { EdgeLocationUsageSection } from './EdgeLocationUsageSection';
import { EdgeLocationStateChipWithWarning } from '../../_components/EdgeLocationStateChip';
import { edgeLocationProviderToAddonsApiProvider } from '../../_components/SelectAddonsField';
import { ReviewInstallationScriptButton } from '../../components/ReviewInstallationScriptButton';
import { CUSTOM_EDGE_PROVIDER, EDGE_PROVIDER_NAME_MAP } from '../../constants';
import { useListAddonsQuery } from 'hooks/queries/omni';
import { EdgeLocationMappedState, EnhancedEdgeLocation } from '../../types';
import { getRegionDisplayName } from '../../utils';

import { useCluster } from '@cast/shared-cluster-context-data-access';

export type EdgeLocationDetailsDrawerProps = DrawerProps & {
  edgeLocation: EnhancedEdgeLocation;
};

const Alerts = ({ edgeLocation }: { edgeLocation: EnhancedEdgeLocation }) => {
  const { cluster } = useCluster();

  if (edgeLocation.provider === CUSTOM_EDGE_PROVIDER) {
    return null;
  }

  switch (edgeLocation.mappedState) {
    case EdgeLocationMappedState.Failed:
      return (
        <Alert
          color="error"
          size="sm"
          testId="edge-location-configuration-error-alert"
          title="Configuration error"
        >
          <Stack gap={12}>
            {edgeLocation.error ||
              'Edge location resource setup is incomplete or invalid. Re-run the installation script to resolve the issue.'}
            <ReviewInstallationScriptButton
              clusterId={cluster.id}
              edgeLocationId={edgeLocation.id!}
              variant="primary"
              size="sm"
              hideSuccessNotification
              text="Rerun onboarding script"
            />
          </Stack>
        </Alert>
      );
    case EdgeLocationMappedState.Pending:
      return (
        <Alert color="warning" size="sm" testId="edge-location-pending-alert">
          <Stack gap={12}>
            Complete the setup by running the installation script in your cloud
            shell or terminal.
            <ReviewInstallationScriptButton
              clusterId={cluster.id}
              edgeLocationId={edgeLocation.id!}
              variant="primary"
              size="sm"
              hideSuccessNotification
            />
          </Stack>
        </Alert>
      );
    default:
      return null;
  }
};

export const EdgeLocationDetailsDrawer = forwardRef<
  HTMLDivElement,
  EdgeLocationDetailsDrawerProps
>(({ edgeLocation, ...props }, ref) => {
  const { cluster } = useCluster();
  const { regions } = useRegionsQuery();

  const isUsageSectionVisible =
    [EdgeLocationMappedState.Ready, EdgeLocationMappedState.InUse].includes(
      edgeLocation.mappedState
    ) && edgeLocation.provider !== CUSTOM_EDGE_PROVIDER;

  const { addons: availableAddons } = useListAddonsQuery({
    provider: edgeLocationProviderToAddonsApiProvider(edgeLocation.provider),
  });

  const addonDisplayNameByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const addon of availableAddons) {
      if (addon.name) {
        map.set(addon.name, addon.displayName ?? addon.name);
      }
    }
    return map;
  }, [availableAddons]);

  return (
    <Drawer
      ref={ref}
      testId="edge-location-details-drawer"
      size="small"
      {...props}
    >
      <DrawerHeader />
      <DrawerBody>
        <DrawerContent px={0}>
          <Stack gap={16} px={24} mb={24}>
            <Stack>
              <Typography
                variant="L12R"
                color={tokens.theme.fg.structural.secondary}
              >
                Edge location
              </Typography>
              <Typography variant="h5" data-testid="edge-location-name">
                {edgeLocation.name}
              </Typography>
            </Stack>
            <EdgeLocationStateChipWithWarning
              edgeLocation={edgeLocation}
              clusterId={cluster.id}
            />
            <Alerts edgeLocation={edgeLocation} />
          </Stack>
          <Divider sx={{ mb: 24 }} />
          <Stack gap={12} mb={24} px={24}>
            <Typography variant="P14B">Description</Typography>
            <Typography
              variant="P14R"
              color="textSecondary"
              data-testid="edge-location-description"
            >
              {edgeLocation.description || '-'}
            </Typography>
          </Stack>
          <Stack
            gap={12}
            padding={24}
            mb={24}
            bgcolor={colorOrToken(
              'grey.50',
              tokens.theme.bg.structural.surfaceRaised
            )}
            borderTop={tokens.theme.border.structural.neutral}
            borderBottom={tokens.theme.border.structural.neutral}
          >
            <Typography
              variant="L12B"
              color={colorOrToken(
                'grey.900',
                tokens.theme.fg.structural.primary
              )}
            >
              Location settings
            </Typography>

            <Detail
              title="Provider:"
              detail={EDGE_PROVIDER_NAME_MAP[edgeLocation.provider]}
            />
            {edgeLocation.provider === Cloud.gcp && (
              <Detail
                title="Provider project ID:"
                detail={edgeLocation.gcp?.projectId ?? '-'}
              />
            )}
            {edgeLocation.provider !== CUSTOM_EDGE_PROVIDER && (
              <Detail
                title="Region:"
                detail={getRegionDisplayName(edgeLocation.region, regions)}
              />
            )}
            <Detail title="ID:" detail={edgeLocation.id ?? '-'} showCopy />
            <Detail
              title="Add-ons:"
              sx={
                edgeLocation.edgeClusterSpec?.addons &&
                edgeLocation.edgeClusterSpec.addons.length > 1
                  ? { alignItems: 'flex-start' }
                  : undefined
              }
              detail={
                edgeLocation.edgeClusterSpec?.addons &&
                edgeLocation.edgeClusterSpec.addons.length > 0 ? (
                  <Stack gap={8} data-testid="edge-location-addons-list">
                    {edgeLocation.edgeClusterSpec.addons.map((addon) => (
                      <Stack
                        key={addon.name}
                        data-testid={`edge-location-addon-${addon.name}`}
                        alignItems="flex-end"
                        gap={4}
                      >
                        <Typography variant="P14R">
                          {addonDisplayNameByName.get(addon.name) ?? addon.name}
                        </Typography>
                        {addon.values && !isEmpty(addon.values) && (
                          <ExpandablePanel
                            title="Helm values"
                            caretPosition="end"
                            testId={`edge-location-addon-values-toggle-${addon.name}`}
                            sx={{ justifyContent: 'flex-end' }}
                          >
                            <PreviewJson
                              data={addon.values}
                              size="md"
                              indentBy={2}
                              interactive={false}
                              testId={`edge-location-addon-values-${addon.name}`}
                              rootSx={{ width: 256, mt: 4 }}
                              height={140}
                            />
                          </ExpandablePanel>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  '-'
                )
              }
            />
          </Stack>

          <Stack px={24} mb={24}>
            <EdgeLocationConfigurationsSection
              edgeLocation={edgeLocation}
              clusterId={cluster.id}
            />
          </Stack>
          <Divider sx={{ mb: 24 }} />
          {isUsageSectionVisible && (
            <>
              <Stack px={24} mb={24}>
                <EdgeLocationUsageSection
                  edgeLocation={edgeLocation}
                  clusterId={cluster.id}
                />
              </Stack>
              <Divider sx={{ mb: 24 }} />
            </>
          )}
          <Stack px={24} mb={24}>
            <EdgeLocationNodesSection
              edgeLocation={edgeLocation}
              clusterId={cluster.id}
              orgId={cluster.organizationId!}
            />
          </Stack>
        </DrawerContent>
      </DrawerBody>
    </Drawer>
  );
});

EdgeLocationDetailsDrawer.displayName = 'EdgeLocationDetailsDrawer';
